/**
 * Audio playback engine — TASK 8.2 (AC 5.4 / 5.5 / 5.7 / 6.4 / 6.5).
 *
 * Replaces the mock's SIMULATED transport — a 1 s `setInterval` position ticker
 * plus `_natEnd`/`_advance`/`_next`/`_prev` over an in-memory array
 * (mock/SteamyMusic.dc.html L896-903) — with one real `HTMLAudioElement` driven
 * by the streaming proxy of TASK 5.1. The queue algebra is kept verbatim; only
 * the sound is new.
 *
 * =========================================================================
 * 1. ONE ELEMENT, FOR THE WHOLE SESSION
 * =========================================================================
 * iOS/Safari only lets audio start inside a user gesture, and the permission
 * attaches to the ELEMENT, not to the page. So the engine creates exactly one
 * `<audio>`, unlocks it on the user's first play, and never makes another:
 * auto-advance, shuffle and repeat all re-point `src` on that same unlocked
 * element and therefore need no fresh gesture (AC 5.4 / 6.5). Creating a new
 * element per track — the obvious alternative — is precisely what breaks
 * background auto-advance on iOS.
 *
 * =========================================================================
 * 2. WHY THERE IS A PREFLIGHT `fetch` BEFORE EVERY `src` ASSIGNMENT
 * =========================================================================
 * A media element's network layer is OPAQUE to JavaScript. `audio.error` is
 * one of `MEDIA_ERR_ABORTED | NETWORK | DECODE | SRC_NOT_SUPPORTED` and
 * nothing else: there is no status code, no response header, no error
 * envelope. But AC 5.5 requires the client to distinguish
 *
 *     410 GONE                      -> no retry, mark unavailable, advance
 *     429 RATE_LIMITED/STREAM_LIMIT/EGRESS_QUOTA -> no retry, halt, per-code
 *                                      message, honour `Retry-After`
 *     anything else                 -> retry once, then skip, then pause
 *
 * and AC 8.x requires `track_play_started.cold_start` to come from the
 * `X-Cold-Start` RESPONSE HEADER (dto.ts: "the client reads the `X-Cold-Start`
 * response header off the STREAM request and reports it here"). None of that
 * is reachable from the element. So each load first issues a TINY RANGED GET
 * of the same URL and reads status + headers + error body from it.
 *
 * ── It is a ranged GET and NOT a HEAD, deliberately ──
 * Story 5 shipped Critical C3 for exactly this: `HEAD /api/stream/:id` is
 * auto-exposed by Fastify, and the handler streams the whole Drive body before
 * Fastify discards it — so a HEAD charges the user's egress ledger for the
 * ENTIRE FILE. A `Range: bytes=0-1` GET is answered by `parseRangeHeader`
 * (services/drive-proxy.ts) as a satisfiable 2-byte window on any file of ≥ 2
 * bytes, so it costs 2 bytes, not 8 MB.
 *
 * ── The honest cost, stated once ──
 * Per track START (not per track, per START), the preflight adds:
 *   * one stream-lease acquire + release (services/stream-lease.ts). The cap is
 *     2 concurrent leases per user, and the preflight's lease is released as
 *     soon as its 2 bytes are delivered, so it cannot lock the user out of
 *     their own next play;
 *   * ~2 bytes of metered egress against the daily ledger;
 *   * one extra Drive round trip;
 *   * and the browser then requests the URL AGAIN for the actual audio. The
 *     bytes are `private, no-store`, so nothing is shared between the two.
 * The alternative designs were weighed and rejected: `fetch`-the-whole-file
 * into a Blob URL buys one request but buffers entire albums in memory and
 * kills range-seeking; MediaSource needs per-codec segmenting we do not have;
 * and doing nothing loses four ACs. If the server ever grows a cheap
 * `GET /api/stream/:id/preflight`, this is the one function to change.
 *
 * ── WHAT THE PREFLIGHT DOES TO `cold_start`, WRITTEN DOWN BEFORE metrics.tf ──
 * `coldStart` is read off the PREFLIGHT response, not off the request the user
 * actually waits on, and those are two different HTTP requests that Cloud Run
 * may route to two different instances. Two consequences, both of which
 * `docs/ops/telemetry-log-contract.md` now records because AC 8.x's SLI is
 * "p95 over `cold_start = false`":
 *
 *   1. When the preflight lands on a COLD instance it reports `true` AND WARMS
 *      IT, so the audio request that follows is very often served warm. The
 *      dimension therefore describes the 2-byte probe's instance, and reading
 *      it as "the listener waited through a cold start" over-counts.
 *   2. When the preflight lands on a warm instance and the audio request is
 *      routed to a cold one, the event says `false` and the SLI under-counts.
 *
 * Neither is fixable from the client without a server-issued correlation id
 * spanning the two requests; `docs/ops/telemetry-log-contract.md` records it as
 * the TASK 11.1 follow-up, together with the fact that `GET /api/stream/:id`
 * is BIMODAL under one route label — a 2-byte range request and a multi-MB
 * stream share it, so its latency histogram has two humps.
 *
 * =========================================================================
 * 3. NO-TEARDOWN ON 429
 * =========================================================================
 * "Already-playing streams are never torn down" (AC 5.5) is not a hope here,
 * it is structural: the preflight runs BEFORE `element.src` is touched, so a
 * refused NEXT track cannot disturb the CURRENT one. When a 429 lands while
 * audio is still playing, the engine records the halt and lets the current
 * track finish; when it lands with nothing playing, it pauses. Either way the
 * per-code banner is shown immediately and no retry is attempted.
 *
 * =========================================================================
 * 4. TESTABILITY
 * =========================================================================
 * jsdom implements no media playback at all (`audio.play()` throws "Not
 * implemented"), so every collaborator is injectable, following the exact
 * shape `src/api/api-client.ts` established: `createAudioEngine(deps)` plus a
 * module-level default singleton the composition root uses. Behaviour is
 * therefore assertable through the REAL seams — dispatching a genuine `ended`
 * event on the injected element and watching the next track load, rather than
 * calling a private `_advance()`.
 *
 * @module
 */

import {
  ERROR_CODES,
  PLAY_EVENT_MS_PLAYED_MAX,
  errorEnvelopeSchema,
  type ErrorCode,
  type PlayEventInitiation,
  type PlayEventInput,
} from '@streamymusic/shared';

import { apiClient, type ApiClient } from '../api/api-client.js';
import { getState, setState } from '../modules/app/state-store.js';
import { codecDetector, type CodecDetector } from './codec-detect.js';

/* ── Types ─────────────────────────────────────────────────────────────── */

/** The subset of `HTMLAudioElement` the engine drives. */
export interface MediaElementLike {
  src: string;
  currentTime: number;
  readonly duration: number;
  readonly paused: boolean;
  readonly error: { readonly code: number } | null;
  play(): Promise<void> | void;
  pause(): void;
  load?(): void;
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

/** A queue item. Mirrors the store's `Track`, plus the codec fields. */
export interface PlayItem {
  readonly id: string;
  readonly title: string;
  readonly artist: string;
  readonly album: string;
  readonly albumId: string;
  /**
   * The album artist's id, carried through to the store's `current` so the
   * player's ⋯ menu can offer "Go to artist" (feature #8). Absent for tracks
   * played from a context with no single artist (e.g. some playlists); the menu
   * item is simply omitted then. The engine never reads it.
   */
  readonly artistId?: string;
  readonly dur: number;
  readonly format?: string;
  readonly formatSupported?: boolean;
  /**
   * The album's cover ref, carried straight through to the store's `current`
   * (`patch({ current: item })`) so the mini/full player can paint the REAL
   * cover art. `null`/absent keeps the gradient. The engine never reads it.
   */
  readonly coverRef?: string | null;
}

/**
 * Where the play came from (§11 `context_type` / `context_id`).
 *
 * A DISCRIMINATED UNION, not `{type?, id?}`. `playEventInputSchema`'s
 * `.superRefine` rejects the whole BATCH — 422, every event lost — when an
 * `album`/`playlist` context arrives without an id, or when any of the other
 * three arrives WITH one. Modelling it as two optional fields made
 * `{type:'queue', id}` and `{type:'album'}` expressible in TypeScript, so the
 * only thing standing between a typo and a silently-dropped batch was a code
 * review. Now `tsc` refuses them.
 */
export type PlayContext =
  /** dto.ts `IDENTIFIED_PLAY_CONTEXT_TYPES` — the id is REQUIRED. */
  | { readonly type: 'album' | 'playlist'; readonly id: string }
  /** The three context types that have nothing to point at — id FORBIDDEN. */
  | { readonly type: 'search' | 'favorites' | 'queue'; readonly id?: never }
  /** No context at all (a bare `playQueue`). */
  | Record<string, never>;

export type RepeatMode = 'off' | 'all' | 'one';

export interface PlayOptions {
  readonly shuffle?: boolean;
  readonly context?: PlayContext;
}

export interface AudioEngine {
  /** Start a collection. THIS is the call that must sit inside a user gesture. */
  playQueue(items: readonly PlayItem[], index: number, options?: PlayOptions): void;
  toggle(): void;
  /**
   * Absolute play / pause for the OS lock-screen controls (Media Session).
   * They act on the audio element's real `paused` state, not the store's
   * `playing` flag, so they stay correct even when iOS has throttled the JS
   * that keeps that flag current — the fix for "lock-screen play/pause does
   * nothing after a locked auto-advance".
   */
  play(): void;
  pause(): void;
  /** Manual skip — always advances, even under repeat-one (AC 6.5). */
  next(): void;
  /** Manual previous — restarts the track when past the 3 s threshold (AC 6.4). */
  previous(): void;
  seek(seconds: number): void;
  setShuffle(on: boolean): void;
  /** off -> all -> one -> off. Returns the new mode. */
  cycleRepeat(): RepeatMode;
  /**
   * The banner's Retry: re-attempts the track that failed. It is also the ONLY
   * way the user can dismiss a banner — §10.6's player banners carry a Retry or
   * nothing, never a close button, so there is no `dismissBanner()` for a UI
   * that has no control to call it. Banners also clear on their own once a
   * subsequent track genuinely reaches `playing` (see `bannerSticky`).
   *
   * REFUSED inside a `Retry-After` window: a 429 names a wait, and honouring it
   * is the whole point of parsing the header (see {@link haltCooldownRemaining}).
   */
  retry(): void;
  /** The one persistent element, exposed so TASK 8.3 can bind Media Session. */
  readonly element: MediaElementLike;
  dispose(): void;
}

/** Just the listener pair — avoids depending on the full DOM `Window`. */
export interface UnloadTargetLike {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface AudioEngineDeps {
  readonly element?: MediaElementLike;
  readonly createElement?: () => MediaElementLike;
  readonly fetchImpl?: typeof fetch;
  readonly api?: Pick<ApiClient, 'sendPlayEvents' | 'handleAuthFailure'> &
    Partial<Pick<ApiClient, 'recordQueueHistory'>>;
  readonly codec?: CodecDetector;
  readonly getState?: typeof getState;
  readonly setState?: typeof setState;
  readonly announce?: (message: string) => void;
  readonly now?: () => number;
  readonly newEventId?: () => string;
  readonly random?: () => number;
  readonly baseUrl?: string;
  /**
   * Where the page-lifecycle flush registers (AC 12.1 / C8). Injectable so the
   * `pagehide` path is driven by a real dispatched event in tests rather than
   * by calling a private function.
   */
  readonly win?: UnloadTargetLike;
  /** Source of `document.visibilityState`; `'hidden'` triggers the same flush. */
  readonly visibilityState?: () => string;
  /** Local diagnostics. Every sibling module takes one; this one used to not. */
  readonly warn?: (message: string, detail: Record<string, string | number>) => void;
  /** Timer seam for the `Retry-After` cooldown and the preflight timeout. */
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
  /** Hard ceiling on one preflight round trip. */
  readonly preflightTimeoutMs?: number;
}

/* ── Constants ─────────────────────────────────────────────────────────── */

/** AC 6.4 — below this many seconds, "previous" means the previous TRACK. */
export const PREVIOUS_RESTART_THRESHOLD_S = 3;
/** AC 5.5 — two consecutive failures pause instead of skipping through. */
export const CONSECUTIVE_FAILURE_LIMIT = 2;
/** A `timeupdate` delta larger than this is a seek, not listening time. */
const MAX_TICK_DELTA_S = 2;

/**
 * How long a 429 blocks new stream requests when the server named no
 * `Retry-After`. A FLOOR, not a guess at the server's window: the point is that
 * "no header" must not mean "hammer immediately".
 *
 * Why any cooldown at all is load-bearing: `GET /api/stream/:trackId` is
 * DELIBERATELY exempt from the request-count rate limiter
 * (`apps/api/src/plugins/ratelimit.ts` — `STREAM_EXEMPT_ROUTE`), and its
 * admission path takes a system-wide `pg_advisory_xact_lock`
 * (`apps/api/src/services/stream-lease.ts`). An always-enabled Retry button
 * therefore lets one user contend a lock every other tenant shares.
 */
export const HALT_COOLDOWN_FLOOR_S = 30;
/**
 * Ceiling on a honoured `Retry-After`. `routes/stream.ts` sets it to
 * `secondsUntilNextUtcMidnight` for `EGRESS_QUOTA` — up to 86 400 s. The engine
 * still SHOWS the server's number, but it will not hold a timer open for a day;
 * after this the user may try again and get a fresh, authoritative 429.
 */
export const HALT_COOLDOWN_CEILING_S = 15 * 60;
/** A preflight that has not answered by now is treated as a transport failure. */
export const PREFLIGHT_TIMEOUT_MS = 15_000;
/**
 * A `pendingSeek` older than this is abandoned. Without a bound, a seek whose
 * `seeked` never arrives freezes the position readout for the rest of the track
 * (the store keeps reporting the requested position while the element has long
 * since moved past it).
 */
export const PENDING_SEEK_TIMEOUT_MS = 10_000;

/**
 * The §11 `error_code` vocabulary this client mints.
 *
 * `PLAY_EVENT_ERROR_CODE_PATTERN`'s own header hands the client the taxonomy
 * ("the client owns the failure taxonomy … `decode_error`, `network_timeout`,
 * `http_410`") because a closed server-side enum would force a coordinated
 * release for every new failure mode. Story 8's first cut collapsed offline,
 * 500, a 502 HTML page, 404, 403, `MEDIA_ERR_NETWORK`, `MEDIA_ERR_ABORTED`,
 * `MEDIA_ERR_SRC_NOT_SUPPORTED` and an autoplay refusal into ONE slug,
 * `playback_error` — which makes the playback-reliability breakdown group
 * everything interesting into a single bar. `result.status` and `result.code`
 * were in scope at the call site and discarded.
 *
 * Every slug produced here satisfies `PLAY_EVENT_ERROR_CODE_PATTERN`
 * (`/^[a-z][a-z0-9_]*$/`), which is asserted directly in the test rig by
 * running the real `playEventInputSchema` over every event the engine emits.
 * The full landed set is documented in `docs/ops/telemetry-log-contract.md`.
 */
export function httpErrorCode(status: number): string {
  if (!Number.isInteger(status) || status < 100 || status > 599) return 'http_unknown';
  return `http_${String(status)}`;
}

/** `MEDIA_ERR_*` (1-4) → a slug. The element offers nothing finer than this. */
export const MEDIA_ERROR_CODES: Readonly<Record<number, string>> = Object.freeze({
  1: 'media_err_aborted',
  2: 'media_err_network',
  3: 'decode_error',
  4: 'media_err_src_not_supported',
});

/** @param code `HTMLMediaElement.error.code`, or `undefined` when absent. */
export function mediaErrorCode(code: number | undefined): string {
  return (code === undefined ? undefined : MEDIA_ERROR_CODES[code]) ?? 'media_err_unknown';
}

/**
 * Slug for a rejected `element.play()`.
 *
 * `NotAllowedError` is the autoplay refusal — the browser declining because the
 * call escaped its user gesture. It is the failure mode this whole
 * one-element-for-the-session architecture exists to survive, so it must be
 * distinguishable in the SLI from a genuine playback fault. There are three
 * `play()` call sites (a fresh load, the repeat-one replay, and `toggle()`) and
 * all three route through here so they cannot drift apart.
 */
export function playRejectionCode(error: unknown): string {
  return (error as { name?: string } | null)?.name === 'NotAllowedError'
    ? 'autoplay_blocked'
    : 'playback_error';
}

/**
 * ui-contract §10.6 "Full player banners" — one distinct message per state.
 * These are the PLAYER strings; §10.2's toast strings for the same 429 codes
 * are deliberately different and live with the toast surface.
 */
export const PLAYER_BANNERS = Object.freeze({
  buffering: 'Buffering…',
  formatUnsupported: 'Format not supported on this device.',
  noLongerAvailable: 'This track is no longer available (removed from Drive).',
  streamLimit: 'Stream limit reached (STREAM_LIMIT). Frees up when a stream ends.',
  egressQuota: 'Daily egress quota exceeded (EGRESS_QUOTA). Resumes after reset.',
  halted: 'Playback paused after two failures in a row.',
});

/** §10.6 renders the retry window inline: "Rate limited (RATE_LIMITED). Retry in ~30s." */
export function rateLimitedBanner(retryAfterSec: number | null): string {
  return retryAfterSec === null
    ? 'Rate limited (RATE_LIMITED).'
    : `Rate limited (RATE_LIMITED). Retry in ~${String(retryAfterSec)}s.`;
}

/** §10.6 error banner: "Couldn't play "15 Step". It'll auto-advance." */
export function couldNotPlayBanner(title: string): string {
  return `Couldn’t play “${title}”. It’ll auto-advance.`;
}

/* ── Preflight ─────────────────────────────────────────────────────────── */

/** Everything the opaque media element cannot tell us. */
export interface PreflightResult {
  readonly ok: boolean;
  readonly status: number;
  /** From `X-Cold-Start`. `undefined` = the header was absent or unparseable. */
  readonly coldStart: boolean | undefined;
  /** The envelope `code`, when the server sent one. */
  readonly code: ErrorCode | null;
  /** Seconds, from `Retry-After`; `null` when absent or not a number. */
  readonly retryAfterSec: number | null;
}

/** `'true'`/`'false'` -> boolean; anything else -> `undefined` (see dto.ts). */
export function parseColdStartHeader(raw: string | null): boolean | undefined {
  if (raw === null) return undefined;
  const value = raw.trim().toLowerCase();
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * `Retry-After` in seconds, in BOTH the forms RFC 9110 §10.2.3 permits.
 *
 * `apps/api` always sends `delay-seconds`, but the header is not necessarily
 * the app's any more by the time it reaches the browser: a CDN, a load
 * balancer or a corporate proxy that synthesises its own 429/503 is entitled
 * to send the HTTP-date form, and parsing only the integer would silently
 * yield `null` — which, now that the value GATES retries, would turn a
 * one-hour wait into an unthrottled retry loop against the lock-taking,
 * rate-limit-exempt stream route.
 *
 * @param now epoch ms, for the date form. Injected so the branch is testable.
 * @returns whole seconds ≥ 0, or `null` when the header is absent/unparseable.
 */
export function parseRetryAfterHeader(raw: string | null, now: number = Date.now()): number | null {
  if (raw === null) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const seconds = Number(trimmed);
  if (Number.isFinite(seconds)) return seconds >= 0 ? Math.round(seconds) : null;

  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  // A date already in the past means "you may retry now", not "never".
  return Math.max(0, Math.round((at - now) / 1000));
}

/** Raised when a preflight exceeded {@link PREFLIGHT_TIMEOUT_MS}. */
export class PreflightTimeoutError extends Error {
  constructor() {
    super('The stream did not answer in time.');
    this.name = 'PreflightTimeoutError';
  }
}

/* ── Engine ────────────────────────────────────────────────────────────── */

export function createAudioEngine(deps: AudioEngineDeps = {}): AudioEngine {
  const {
    fetchImpl = (...args: Parameters<typeof fetch>) => globalThis.fetch(...args),
    api = apiClient,
    codec = codecDetector,
    getState: readState = getState,
    setState: writeState = setState,
    announce = (message: string) => {
      writeState({ live: message });
    },
    now = () => Date.now(),
    newEventId = () => globalThis.crypto.randomUUID(),
    random = () => Math.random(),
    baseUrl = '',
    createElement = () => new Audio(),
    win = globalThis,
    visibilityState = () =>
      typeof document === 'undefined' ? 'visible' : document.visibilityState,
    warn = (message: string, detail: Record<string, string | number>) => {
      console.warn(message, detail);
    },
    setTimer = (fn: () => void, ms: number) => setTimeout(fn, ms),
    clearTimer = (handle: unknown) => {
      clearTimeout(handle as ReturnType<typeof setTimeout>);
    },
    preflightTimeoutMs = PREFLIGHT_TIMEOUT_MS,
  } = deps;

  /** THE element. Created once, lazily, and reused for the whole session. */
  let element: MediaElementLike | null = deps.element ?? null;
  function media(): MediaElementLike {
    if (element === null) {
      element = createElement();
      attach(element);
    }
    return element;
  }

  /* ── engine-local state (everything the STORE does not own) ──────────── */

  /** The collection a repeat-all wrap restarts from (the mock's `state.ctx`). */
  let context: PlayItem[] = [];
  let playContext: PlayContext = {};
  let consecutiveFailures = 0;
  /** Set by a 429: blocks auto-advance without touching a playing stream. */
  let halted = false;
  /**
   * Epoch ms until which a 429's `Retry-After` refuses every new stream
   * request — `load()`, `retry()` and `next()` all consult it. Before this
   * existed the header was parsed, stored on the banner, rendered in prose and
   * then IGNORED: ten Retry presses inside a `Retry-After: 3600` window issued
   * eleven stream requests.
   */
  let haltedUntil: number | null = null;
  /** The banner kind a cooldown repaint should keep showing. */
  let haltKind: NonNullable<Playback['banner']>['kind'] = 'rate-limited';
  /** Handle for the timer that re-enables Retry when the window elapses. */
  let cooldownTimer: unknown = null;
  /** The item a banner Retry would re-attempt. */
  let lastFailed: PlayItem | null = null;
  /**
   * A failure/halt notice OUTLIVES the auto-advance it triggers. AC 5.5 calls
   * the one-failure case "non-blocking", which is only true if the user can
   * still read it while the next track plays; a banner cleared by the very
   * advance it announced would be invisible.
   *
   * BOUNDED, though. It is cleared by Retry, by the next deliberate
   * `playQueue`, and — the case that used to be missing entirely — as soon as
   * a SUBSEQUENT track genuinely reaches `playing`. Without that last exit a
   * `no-longer-available` or `format-unsupported` notice (both emitted with
   * `retry: false`, and §10.6 banners have no close button) could never leave
   * the screen: it sat above healthy audio for the rest of the session.
   */
  let bannerSticky = false;
  /** Aborts a preflight the user has already skipped past. */
  let inflight: AbortController | null = null;
  /** Monotonic load id — a late preflight for a superseded track is ignored. */
  let loadToken = 0;
  /** Which attempt the CURRENT src belongs to, so the element's own `error`
   *  event honours the same "retry exactly once" budget as the preflight. */
  let currentAttempt = 0;
  /** Seek target held until `seeked` lands, so the UI never snaps to 0:00. */
  let pendingSeek: number | null = null;
  /** When {@link pendingSeek} was issued, so a `seeked` that never comes expires. */
  let pendingSeekAt = 0;
  /** Last position written to `element.currentTime`; suppresses redundant writes. */
  let lastSeekWrite: number | null = null;
  /** Accounting for the current play's cumulative `msPlayed` (AC 12.1). */
  let playedMs = 0;
  let lastTickAt: number | null = null;
  let startedAt: string | null = null;
  let currentColdStart: boolean | undefined;
  let currentInitiation: PlayEventInitiation = 'user';
  /** Guards against reporting the same play twice (ended + a manual skip). */
  let reported = true;
  let disposed = false;

  /* ── store helpers ──────────────────────────────────────────────────── */

  type Playback = ReturnType<typeof readState>['playback'];

  function playback(): Playback {
    return readState().playback;
  }

  function patch(next: Partial<Playback>): void {
    // Every consumer — the shell, the full player, the Media Session bridge —
    // reads the STORE, so there is no second notification channel that could
    // fall out of step with it.
    writeState((state) => ({ playback: { ...state.playback, ...next } }));
  }

  function setBanner(
    kind: NonNullable<Playback['banner']>['kind'],
    message: string,
    retry: boolean,
    retryAfterSec?: number,
  ): void {
    patch({
      banner: {
        kind,
        message,
        retry,
        ...(retryAfterSec === undefined ? {} : { retryAfterSec }),
      },
    });
    bannerSticky = true;
    announce(message);
  }

  /** mock L897 `_shuf` — Fisher-Yates on a copy, with an injectable RNG. */
  function shuffled(input: readonly PlayItem[]): PlayItem[] {
    const out = [...input];
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1));
      [out[i], out[j]] = [out[j] as PlayItem, out[i] as PlayItem];
    }
    return out;
  }

  /* ── telemetry (§11 track_play_* via POST /api/play-events) ──────────── */

  /**
   * Builds ONE play event. The server derives `track_play_started` from every
   * event, `track_play_counted` when it crosses the AC 12.1 threshold, and
   * `track_play_failed` when `errorCode` is present.
   *
   * ONE EVENT PER ATTEMPT — and that is now true. It was not: the element's own
   * `error` handler used to silently re-`load()` the same track with no event
   * at all, and `halt()` painted a 429 banner without one either, so the two
   * failure families the reliability SLI most needs were invisible. Both now
   * report before they act.
   */
  function buildEvent(
    item: PlayItem,
    fields: { msPlayed: number; playedAt: string; errorCode?: string },
  ): PlayEventInput {
    return {
      clientEventId: newEventId(),
      trackId: item.id,
      playedAt: fields.playedAt,
      // CLAMPED to the schema's ceiling. `msPlayed` over 24 h is a 422 on the
      // WHOLE batch (`playEventInputSchema`), which would silently drop every
      // healthy event travelling with it — and `codec-detect.ts` admits `m4b`,
      // so a >24 h audiobook is not hypothetical.
      msPlayed: Math.min(PLAY_EVENT_MS_PLAYED_MAX, Math.max(0, Math.round(fields.msPlayed))),
      source: 'stream',
      initiation: currentInitiation,
      // `coldStart` is OMITTED, never `false`, when the header was absent —
      // dto.ts is explicit that "unknown" must not be guessed as `false` or the
      // AC 8.x latency SLI silently absorbs the unknowns.
      ...(currentColdStart === undefined ? {} : { coldStart: currentColdStart }),
      ...(playContext.type === undefined ? {} : { contextType: playContext.type }),
      ...(playContext.id === undefined ? {} : { contextId: playContext.id }),
      ...(fields.errorCode === undefined ? {} : { errorCode: fields.errorCode }),
    };
  }

  /**
   * Posts one event. Fire-and-forget and swallowed on failure: diagnostics must
   * never break playback (the rule `lib/telemetry-client.ts` follows).
   */
  function sendEvent(
    item: PlayItem,
    fields: { msPlayed: number; playedAt: string; errorCode?: string },
  ): void {
    void Promise.resolve(api.sendPlayEvents([buildEvent(item, fields)])).catch(() => {
      /* diagnostics only — never surfaced, never retried into a storm */
    });
  }

  /**
   * Reports the play that just ended. Idempotent: `ended` + a skip is ONE play.
   * `reason` records WHY it left the deck for the queue history (feature #5):
   * `'finished'` when the track ran to its end, `'skipped'` when the user moved
   * on. Because this is idempotent, each played track lands in the history
   * exactly once, with the reason of whichever event fired first.
   *
   * @param reason why the track left — feeds the queue history
   */
  function reportPlay(item: PlayItem, reason: 'finished' | 'skipped' = 'finished'): void {
    if (reported) return;
    reported = true;
    sendEvent(item, {
      msPlayed: playedMs,
      playedAt: startedAt ?? new Date(now()).toISOString(),
    });
    // #5 — append the played track to the queue history. Fire-and-forget and
    // swallowed: a history write must never disturb playback (same rule as the
    // play-event post above). The api-client mints the idempotency key.
    void Promise.resolve(api.recordQueueHistory?.({ trackId: item.id, reason })).catch(() => {
      /* diagnostics only — the history entry is best-effort */
    });
  }

  /**
   * Reports a FAILED attempt. Always sends — a track that never started has no
   * earlier event to deduplicate against, and `track_play_failed` is the
   * numerator of the reliability SLI, so a dropped one is a silent hole.
   */
  function reportFailure(item: PlayItem, errorCode: string): void {
    const isCurrent = (playback().current)?.id === item.id;
    const msPlayed = isCurrent && !reported ? playedMs : 0;
    if (isCurrent) reported = true;
    sendEvent(item, {
      msPlayed,
      playedAt: isCurrent && startedAt !== null ? startedAt : new Date(now()).toISOString(),
      errorCode,
    });
  }

  function beginAccounting(initiation: PlayEventInitiation): void {
    playedMs = 0;
    lastTickAt = null;
    startedAt = new Date(now()).toISOString();
    currentInitiation = initiation;
    reported = false;
  }

  /* ── page-lifecycle flush (AC 12.1) ──────────────────────────────────────
   *
   * `reportPlay` only ever fired from `ended`, `next()` and `previous()`, so
   * the LAST play of every session was never sent: the user closes the tab,
   * switches app, or locks the phone, and the listen is simply lost. On iOS —
   * where `media-session.ts` documents that a backgrounded PWA is
   * "aggressively suspended" and `ended` may never fire at all — that can be
   * MOST plays, which makes the AC 12.1 counted-play number an undercount of
   * unknown size.
   *
   * ── WHY NOT `navigator.sendBeacon` ────────────────────────────────────────
   * `sendBeacon` is the textbook answer and it CANNOT WORK HERE.
   * `apps/api/src/plugins/csrf.ts` rejects every POST whose `x-csrf-token`
   * header does not match the cookie, in an onRequest hook, before any handler
   * runs — and `sendBeacon` takes no headers at all. A beacon to
   * `/api/play-events` is a guaranteed 403, i.e. the same lost play with extra
   * steps. `fetch(..., { keepalive: true })` is the form that survives page
   * teardown AND carries headers, so delivery goes through the api-client
   * (which owns the CSRF header, `same-origin` credentials and the global
   * 401/403 handling) with `keepalive` set. See the task report.
   */

  /** Flushes the in-progress play. Idempotent — `reported` guards double counts. */
  function flushOnUnload(): void {
    const item = playback().current;
    if (item === null || reported) return;
    reported = true;
    const event = buildEvent(item, {
      msPlayed: playedMs,
      playedAt: startedAt ?? new Date(now()).toISOString(),
    });
    void Promise.resolve(api.sendPlayEvents([event], { keepalive: true })).catch(() => {
      /* the page is going away; there is nowhere to report a failure to */
    });
  }

  const handleUnload = (): void => {
    flushOnUnload();
  };
  const handleVisibility = (): void => {
    // `visibilitychange` → hidden is the only lifecycle signal iOS Safari
    // reliably delivers before a suspend; `pagehide` covers bfcache + close.
    if (visibilityState() === 'hidden') {
      flushOnUnload();
      return;
    }
    // Back in the foreground (screen unlocked): the element kept its own truth
    // through the throttle — snap the store to it so the transport is correct.
    reconcileFromElement();
  };

  win.addEventListener('pagehide', handleUnload);
  win.addEventListener('visibilitychange', handleVisibility);

  /* ── Retry-After cooldown ────────────────────────────────────────────── */

  /** Whole seconds still to run on the current 429 window; 0 when clear. */
  function haltCooldownRemaining(): number {
    if (haltedUntil === null) return 0;
    const remaining = Math.ceil((haltedUntil - now()) / 1000);
    if (remaining <= 0) {
      haltedUntil = null;
      return 0;
    }
    return remaining;
  }

  /** The §10.6 message for a halt kind, with the wait interpolated when known. */
  function haltMessage(kind: NonNullable<Playback['banner']>['kind'], remaining: number): string {
    if (kind === 'stream-limit') return PLAYER_BANNERS.streamLimit;
    if (kind === 'egress-quota') return PLAYER_BANNERS.egressQuota;
    return rateLimitedBanner(remaining > 0 ? remaining : null);
  }

  /**
   * Repaints the halt banner with the seconds still to run and whether Retry is
   * usable yet. `retryDisabled` is what the full player binds `disabled` to, so
   * the control the user reaches for is visibly refused rather than silently
   * ignored — for ALL THREE 429 codes, not just `RATE_LIMITED`.
   */
  function paintHaltBanner(remaining: number, announceIt: boolean): void {
    const message = haltMessage(haltKind, remaining);
    patch({
      banner: {
        kind: haltKind,
        message,
        retry: true,
        retryDisabled: remaining > 0,
        ...(remaining > 0 ? { retryAfterSec: remaining } : {}),
      },
    });
    bannerSticky = true;
    if (announceIt) announce(message);
  }

  /**
   * Gate for every path that would start a stream. Returns true — and refreshes
   * the banner — while a `Retry-After` window is still running.
   */
  function blockedByCooldown(): boolean {
    const remaining = haltCooldownRemaining();
    if (remaining === 0) return false;
    paintHaltBanner(remaining, true);
    patch({ buffering: false });
    return true;
  }

  /* ── element events ─────────────────────────────────────────────────── */

  const handlers: Record<string, (event: Event) => void> = {
    timeupdate: () => {
      const el = media();
      const at = el.currentTime;
      // Accumulate LISTENING time, not wall-clock: a backwards seek or a long
      // stall must not inflate `msPlayed` past what was actually heard.
      if (lastTickAt !== null) {
        const delta = at - lastTickAt;
        if (delta > 0 && delta <= MAX_TICK_DELTA_S) playedMs += delta * 1000;
      }
      lastTickAt = at;
      // A seek whose `seeked` never lands must not freeze the readout for the
      // rest of the track. Expiry is measured off the injected clock rather
      // than a timer, so there is nothing to leak and nothing to fake.
      if (pendingSeek !== null && now() - pendingSeekAt >= PENDING_SEEK_TIMEOUT_MS) {
        pendingSeek = null;
      }
      // While a seek is outstanding the element still reports the OLD position;
      // showing it would make the scrubber jump back (AC 6.8).
      if (pendingSeek === null) patch({ pos: at });
    },
    durationchange: () => {
      const duration = media().duration;
      if (Number.isFinite(duration) && duration > 0) patch({ dur: duration });
    },
    waiting: () => {
      patch({ buffering: true });
    },
    playing: () => {
      // Audio is genuinely flowing, so whatever position the element is at IS
      // the truth — including after a `resumeAt` reload, which produces no
      // `seeked` of its own.
      pendingSeek = null;
      patch({ buffering: false, playing: true });
    },
    canplay: () => {
      patch({ buffering: false });
    },
    seeking: () => {
      patch({ buffering: true });
    },
    seeked: () => {
      pendingSeek = null;
      lastTickAt = media().currentTime;
      patch({ buffering: false, pos: media().currentTime });
    },
    pause: () => {
      // AC 6.7 — an interruption (call, another app, headphones out) arrives
      // as a plain `pause`. The POSITION is preserved so resume continues.
      patch({ playing: false, pos: media().currentTime });
    },
    ended: () => {
      const item = playback().current;
      if (item !== null) reportPlay(item, 'finished');
      if (halted) {
        // A 429 landed while this track was still playing: it was allowed to
        // finish (no teardown), and the halt applies at this boundary.
        patch({ playing: false });
        return;
      }
      if (playback().repeat === 'one') {
        // Same element, same src — no new gesture is needed (AC 5.4/6.5).
        const el = media();
        el.currentTime = 0;
        beginAccounting('auto');
        announce(`Repeating ${item?.title ?? ''}`.trim());
        void Promise.resolve(el.play()).catch((error: unknown) => {
          if (item !== null) failTrack(item, playRejectionCode(error), couldNotPlayBanner(item.title));
        });
        return;
      }
      advance('auto');
    },
    error: () => {
      const el = media();
      const item = playback().current;
      if (item === null) return;
      // The element cannot say WHY beyond a four-value enum; the preflight
      // already ruled out 410/429, so this is a transport/decode failure. The
      // enum member is carried through rather than flattened, so a decode bug
      // and a mid-stream socket reset are distinguishable in the SLI.
      const errorCode = mediaErrorCode(el.error?.code);
      if (currentAttempt === 0) {
        /*
         * TWO things the silent re-`load()` used to throw away.
         *
         * (1) THE LISTEN. `load()` calls `beginAccounting()`, which resets
         *     `playedMs = 0` and `reported = false` — so 60 s of real listening
         *     followed by one `error` produced NO event at all, and the
         *     successful reload then reported `msPlayed: 0`. AC 12.1's
         *     counted-play threshold could never be crossed by any track that
         *     hiccupped once. Reporting first both preserves the partial listen
         *     and gives the reliability SLI the recovered failure it was
         *     missing entirely.
         * (2) THE POSITION. Reassigning `src` restarts at 0:00 and re-downloads
         *     the whole file — the exact behaviour AC 6.8 forbids, and a real
         *     cost against the egress ledger. The pre-error position is carried
         *     into the retry.
         */
        reportFailure(item, errorCode);
        const resumeAt = Number.isFinite(el.currentTime) ? el.currentTime : 0;
        void load(item, currentInitiation, 1, resumeAt);
        return;
      }
      failTrack(item, errorCode, couldNotPlayBanner(item.title));
    },
  };

  function attach(target: MediaElementLike): void {
    for (const [type, handler] of Object.entries(handlers)) {
      target.addEventListener(type, handler);
    }
  }

  if (element !== null) attach(element);

  /* ── loading ────────────────────────────────────────────────────────── */

  function streamUrl(trackId: string): string {
    return `${baseUrl}/api/stream/${encodeURIComponent(trackId)}`;
  }

  /**
   * The tiny ranged GET described in the module header. Returns everything the
   * media element cannot report; a transport failure throws.
   *
   * ── IT DOES NOT GO THROUGH `api-client.execute()`, AND WHY THAT IS OK ─────
   * `execute()` reads the whole body and parses a JSON envelope; this request
   * deliberately wants the RAW `Response` (status, `X-Cold-Start`,
   * `Retry-After`) and must NOT consume 2 bytes of audio as JSON. What it must
   * not fork is the 403 DISCRIMINATION the api-client's header spends forty
   * lines establishing — so instead of reimplementing it, the outcome is handed
   * back to the client's own {@link ApiClient.handleAuthFailure}, which runs
   * the identical `/api/me` probe, the identical revocation purge and the
   * identical `#/signin` bounce. Before this, a session expiring mid-album
   * showed "paused after two failures in a row" and a revocation never reached
   * the purge path at all.
   */
  async function preflight(trackId: string, signal: AbortSignal): Promise<PreflightResult> {
    const response = await fetchImpl(streamUrl(trackId), {
      method: 'GET',
      credentials: 'same-origin',
      // 2 bytes. NOT a HEAD — see the module header (Story 5 Critical C3).
      headers: { range: 'bytes=0-1', accept: '*/*' },
      signal,
    });

    const coldStart = parseColdStartHeader(response.headers.get('x-cold-start'));
    const retryAfterSec = parseRetryAfterHeader(response.headers.get('retry-after'), now());

    let code: ErrorCode | null = null;
    if (!response.ok) {
      try {
        const parsed = errorEnvelopeSchema.safeParse(await response.json());
        if (parsed.success) code = parsed.data.code;
      } catch {
        // A non-JSON error body is not a reason to lose the STATUS, which is
        // what the branch below actually keys on.
      }
    } else {
      /*
       * CANCEL THE BODY. Nothing reads these 2 bytes, and an unread `Response`
       * body holds its connection open until GC — one dangling stream per track
       * START, against a browser budget of ~6 connections per origin. Left
       * alone, half a dozen skips can stall every other request on the page,
       * including the audio the user is waiting for.
       */
      void response.body?.cancel().catch(() => {
        /* already consumed or unsupported (jsdom): nothing to release */
      });
    }

    if (response.status === 401 || response.status === 403) {
      await api.handleAuthFailure(response.status, `/api/stream/${trackId}`);
    }

    return { ok: response.ok, status: response.status, coldStart, code, retryAfterSec };
  }

  /**
   * Runs the preflight under a WALL-CLOCK bound.
   *
   * The `AbortController` already existed, but only a newer load or `dispose()`
   * ever fired it — never a clock. A hung Drive round trip therefore left
   * `buffering: true` in the store forever: a spinner with no timeout, no
   * banner and no way out, which is the one outcome §10.6 says every surface
   * must avoid.
   */
  async function preflightWithTimeout(
    trackId: string,
    controller: AbortController,
  ): Promise<PreflightResult> {
    let timedOut = false;
    const timer = setTimer(() => {
      timedOut = true;
      controller.abort();
    }, preflightTimeoutMs);
    try {
      return await preflight(trackId, controller.signal);
    } catch (error) {
      if (timedOut) throw new PreflightTimeoutError();
      throw error;
    } finally {
      clearTimer(timer);
    }
  }

  /**
   * Loads and starts `item` on the persistent element.
   *
   * @param attempt 0 = first try, 1 = the single permitted retry (AC 5.5).
   * @param resumeAt seconds to restore after re-pointing `src` at the same
   *   track (the element-`error` retry). 0 for a genuine new start.
   */
  async function load(
    item: PlayItem,
    initiation: PlayEventInitiation,
    attempt = 0,
    resumeAt = 0,
  ): Promise<void> {
    const token = (loadToken += 1);
    currentAttempt = attempt;
    inflight?.abort();
    const controller = new AbortController();
    inflight = controller;

    /*
     * SET HERE, NOT IN `beginAccounting()`. `beginAccounting` runs only AFTER a
     * successful preflight, so every failure that happened before the element
     * was touched — 410, format-unsupported, a non-ok status, a transport
     * throw — reported the PREVIOUS track's initiation, or the `'user'`
     * default. dto.ts calls this field "LOAD-BEARING, not a nice-to-have label"
     * and names `auto` starts "the starts a user never asked for and therefore
     * the ones a reliability regression hides in"; the client was
     * systematically relabelling exactly those.
     */
    currentInitiation = initiation;

    const superseded = (): boolean => disposed || token !== loadToken;

    // AC 5.7 — the runtime probe decides, before a byte is requested. A device
    // that cannot decode the container gets an explicit marker, never a stream
    // that stalls behind a spinner forever.
    if (item.format !== undefined && !codec.supports(item.format, item.formatSupported)) {
      failTrack(item, 'format_unsupported', PLAYER_BANNERS.formatUnsupported, 'format-unsupported');
      return;
    }

    // A 429 named a wait. Until it elapses, no start issues any network work.
    if (blockedByCooldown()) return;

    patch({ buffering: true, ...(bannerSticky ? {} : { banner: null }) });

    /*
     * SERIALISE THE BURST BEFORE ANY NETWORK WORK.
     *
     * `load()` preflights before touching the element, so the currently-playing
     * track's lease is still open while the preflight takes a second one — and
     * the server only frees a lease when the socket close reaches it and
     * `settle()` runs (`apps/api/src/routes/stream.ts`). N rapid skips
     * therefore transiently WANT N+1 leases against a per-user cap of 2, and
     * the resulting 429 pins a banner for the whole cooldown.
     *
     * Yielding one microtask here means a burst of synchronous `next()` presses
     * all bump `loadToken` first, and every superseded start returns before it
     * fetches anything: three presses issue ONE preflight, not three.
     *
     * What this deliberately does NOT do is clear `element.src` first to free
     * the old lease. That would tear down a stream that is still playing, which
     * is precisely the AC 5.5 guarantee ("already-playing streams are never
     * torn down") the no-teardown test pins.
     */
    await Promise.resolve();
    if (superseded()) return;

    let result: PreflightResult;
    try {
      result = await preflightWithTimeout(item.id, controller);
    } catch (error) {
      if (superseded()) return;
      if ((error as { name?: string }).name === 'AbortError') return;
      if (error instanceof PreflightTimeoutError) {
        warn('audio-engine: preflight timed out', { trackId: item.id, ms: preflightTimeoutMs });
        retryOrFail(item, initiation, attempt, 'network_timeout');
        return;
      }
      // A `fetch` that rejects is the transport, not the server: DNS, TLS, or
      // simply no network. `navigator.onLine === false` is the one case the
      // browser will actually tell us about, and it is worth its own slug.
      const offline = globalThis.navigator?.onLine === false;
      retryOrFail(item, initiation, attempt, offline ? 'network_offline' : 'network_error');
      return;
    }
    if (superseded()) return;

    if (result.status === 410 || result.code === ERROR_CODES.GONE) {
      // Permanent. Retrying cannot help, so it never happens (AC 5.5).
      failTrack(item, 'gone', PLAYER_BANNERS.noLongerAvailable, 'no-longer-available');
      return;
    }

    if (result.status === 429) {
      halt(item, result.code, result.retryAfterSec);
      return;
    }

    if (result.status === 401 || result.status === 403) {
      // The api-client has already navigated (and purged, if this was a
      // revocation). Spending the retry budget on a request whose outcome is
      // now known would just be a second stream request on the way out.
      failTrack(item, httpErrorCode(result.status), couldNotPlayBanner(item.title));
      return;
    }

    if (!result.ok) {
      // The STATUS was in scope here and used to be discarded into a single
      // `playback_error` bucket alongside decode faults and autoplay refusals.
      retryOrFail(item, initiation, attempt, httpErrorCode(result.status));
      return;
    }

    // Only NOW is the element touched — which is what makes "already-playing
    // streams are never torn down" structural rather than aspirational.
    currentColdStart = result.coldStart;
    const el = media();
    el.src = streamUrl(item.id);
    pendingSeek = null;
    lastSeekWrite = null;
    beginAccounting(initiation);
    if (resumeAt > 0) {
      // Restore before `play()` so the element never starts at 0:00 and pulls
      // the whole file down again. `pendingSeek` keeps the readout at the
      // resume point until the element reports `playing` from there.
      el.currentTime = resumeAt;
      pendingSeek = resumeAt;
      pendingSeekAt = now();
      lastTickAt = resumeAt;
    }
    patch({
      current: item,
      pos: resumeAt,
      dur: item.dur,
      buffering: true,
      ...(bannerSticky ? {} : { banner: null }),
    });

    try {
      await el.play();
      if (superseded()) return;
      consecutiveFailures = 0;
      lastFailed = null;
      /*
       * A TRACK IS GENUINELY PLAYING — so the previous track's notice has been
       * read and has done its job. This is the exit `bannerSticky` never had:
       * `no-longer-available` and `format-unsupported` are emitted with
       * `retry: false` and §10.6 banners carry no close button, so without it
       * a 410 notice stayed on screen above healthy audio for the rest of the
       * session. The notice still OUTLIVES the advance it triggered (it is
       * preserved through the `patch` above while the next track loads); it
       * just no longer outlives the track.
       */
      bannerSticky = false;
      patch({ playing: true, buffering: false, banner: null });
      announce(`Now playing ${item.title}`);
    } catch (error) {
      if (superseded()) return;
      if ((error as { name?: string }).name === 'AbortError') return;
      // `play()` rejecting is overwhelmingly the iOS/Safari autoplay refusal —
      // a NotAllowedError raised because the call escaped its user gesture.
      // That is a different operational problem from a decode fault and gets
      // its own slug rather than disappearing into `playback_error`.
      retryOrFail(item, initiation, attempt, playRejectionCode(error));
    }
  }

  /** AC 5.5's "retry once" — bounded to a single re-attempt per track. */
  function retryOrFail(
    item: PlayItem,
    initiation: PlayEventInitiation,
    attempt: number,
    errorCode: string,
  ): void {
    if (attempt === 0) {
      void load(item, initiation, 1);
      return;
    }
    failTrack(item, errorCode, couldNotPlayBanner(item.title));
  }

  /**
   * One track has definitively failed.
   *
   * The first failure is NON-BLOCKING — a message plus auto-advance. The
   * second CONSECUTIVE one pauses, which is what stops the engine skipping
   * silently through an entire unplayable album (AC 5.5). 410 and
   * format-unsupported count towards it for exactly that reason: a whole album
   * removed from Drive would otherwise scroll past in a second.
   */
  function failTrack(
    item: PlayItem,
    errorCode: string,
    message: string,
    kind: NonNullable<Playback['banner']>['kind'] = 'error',
  ): void {
    lastFailed = item;
    reportFailure(item, errorCode);
    consecutiveFailures += 1;

    if (consecutiveFailures >= CONSECUTIVE_FAILURE_LIMIT) {
      media().pause();
      patch({ playing: false, buffering: false });
      setBanner('playback-halted', PLAYER_BANNERS.halted, true);
      return;
    }

    setBanner(kind, message, kind === 'error');
    // The failed `load()` raised `buffering` before it started. Nothing else
    // lowers it when the queue turns out to be empty, so a last-track failure
    // used to leave the spinner up under the banner explaining the failure.
    patch({ buffering: false });
    advance('auto');
  }

  /** 429 — no retry, per-code banner, `Retry-After` ENFORCED, no teardown. */
  function halt(item: PlayItem, code: ErrorCode | null, retryAfterSec: number | null): void {
    halted = true;
    // The refused track is what the banner's Retry re-attempts.
    lastFailed = item;
    const el = media();

    /*
     * REPORT IT. `halt()` used to set state, paint a banner and return — no
     * `track_play_failed` at all — so the three 429 codes, the exact failure
     * family Story 5 shipped a Critical over, were invisible in production.
     * dto.ts calls `errorCode` "the field the playback-reliability breakdown
     * groups by", and `track_play_failed` is the SLI numerator; this module's
     * own comment says "a dropped one is a silent hole". All three slugs
     * satisfy `PLAY_EVENT_ERROR_CODE_PATTERN`.
     */
    if (code === ERROR_CODES.STREAM_LIMIT) haltKind = 'stream-limit';
    else if (code === ERROR_CODES.EGRESS_QUOTA) haltKind = 'egress-quota';
    else haltKind = 'rate-limited';
    const slug =
      haltKind === 'stream-limit'
        ? 'stream_limit'
        : haltKind === 'egress-quota'
          ? 'egress_quota'
          : 'rate_limited';
    reportFailure(item, slug);

    /*
     * ENFORCE the wait. `Retry-After` was parsed, stored on the banner and
     * rendered in prose, and then nothing read it: `retry()` reset the state
     * and re-issued `load()` immediately, and the full player rendered an
     * always-enabled Retry. Ten presses inside a `Retry-After: 3600` window
     * issued eleven requests to a route that is exempt from the request-count
     * limiter and takes a system-wide advisory lock.
     */
    const requested = retryAfterSec ?? HALT_COOLDOWN_FLOOR_S;
    const cooldownS = Math.min(
      HALT_COOLDOWN_CEILING_S,
      Math.max(HALT_COOLDOWN_FLOOR_S, requested),
    );
    haltedUntil = now() + cooldownS * 1000;
    // SHOW the server's own number even when it is larger than the timer we
    // are willing to hold — the user should read the truth, not our ceiling.
    paintHaltBanner(retryAfterSec ?? cooldownS, true);

    if (cooldownTimer !== null) clearTimer(cooldownTimer);
    cooldownTimer = setTimer(() => {
      cooldownTimer = null;
      if (disposed) return;
      haltedUntil = null;
      // Re-enable the control the user is waiting on, without re-announcing:
      // an unprompted second read-out of the same message is noise.
      if (playback().banner?.kind === haltKind) paintHaltBanner(0, false);
    }, cooldownS * 1000);

    // The element is NOT touched when something is still playing: the refusal
    // was for a stream that never started (AC 5.5 "no teardown"). The halt is
    // applied at the next boundary by the `ended` handler.
    if (el.paused) patch({ playing: false, buffering: false });
    else patch({ buffering: false });
  }

  /* ── transport ──────────────────────────────────────────────────────── */

  /**
   * mock L899 `_advance` — take the head of the queue, else wrap under
   * repeat-all (RESHUFFLING when shuffle is on, AC 6.5), else stop.
   */
  function advance(initiation: PlayEventInitiation): void {
    if (halted) {
      patch({ playing: false });
      return;
    }
    const state = playback();
    const queue = [...(state.queue as PlayItem[])];
    const past = [...(state.past as PlayItem[])];
    const current = state.current;
    if (current !== null) past.push(current);

    const nextItem = queue.shift();
    if (nextItem !== undefined) {
      patch({ queue, past });
      void load(nextItem, initiation);
      return;
    }

    if (state.repeat === 'all' && context.length > 0) {
      const order = state.shuffle ? shuffled(context) : [...context];
      const first = order[0] as PlayItem;
      patch({ queue: order.slice(1), past: [] });
      void load(first, initiation);
      return;
    }

    // Nothing left and no repeat: stop at the end, exactly like the mock.
    patch({ playing: false, pos: state.dur });
  }

  /* ── play / pause on the ELEMENT's truth, not the store flag ────────────
   *
   * `toggle()` used to branch on `playback().playing`. That store flag drifts
   * from reality whenever iOS throttles JavaScript with the screen locked: the
   * element keeps playing (or auto-advances) in the OS media layer while the
   * `playing`/`pause` events that would update the flag are delayed. A
   * lock-screen play/pause (Media Session) that consults the stale flag then
   * decides "already paused, do nothing" and the control appears dead — the
   * reported "play/pause does not affect the track". Reading `el.paused`, the
   * element's own source of truth, makes the intent absolute and correct
   * regardless of how stale the store is.
   */
  function doPlay(): void {
    const el = media();
    if (playback().current === null) return;
    if (!el.paused) {
      patch({ playing: true }); // already flowing — just reconcile the flag
      return;
    }
    void Promise.resolve(el.play())
      .then(() => patch({ playing: true }))
      .catch((error: unknown) => {
        const item = playback().current;
        if (item !== null) failTrack(item, playRejectionCode(error), couldNotPlayBanner(item.title));
      });
  }

  function doPause(): void {
    const el = media();
    if (playback().current === null) return;
    if (el.paused) {
      patch({ playing: false });
      return;
    }
    el.pause();
    patch({ playing: false, pos: el.currentTime });
  }

  /**
   * Snap the store back to the ELEMENT after the app returns from being hidden.
   * iOS throttles JS while locked, so `playing`/`pos` can drift from what the
   * element actually did (it kept playing, or auto-advanced, in the OS media
   * layer). On `visibilitychange` → visible this re-reads the truth so the
   * in-app transport and scrubber are correct the instant the screen comes back.
   */
  function reconcileFromElement(): void {
    if (disposed || element === null) return;
    if (playback().current === null) return;
    const el = element;
    const actuallyPlaying = !el.paused;
    const raw = el.currentTime;
    const pos = Number.isFinite(raw) ? raw : playback().pos;
    const next: Partial<Playback> = {};
    if (playback().playing !== actuallyPlaying) next.playing = actuallyPlaying;
    // Leave an in-flight seek alone — its target, not the element's old reading,
    // is the truth until `seeked` lands (AC 6.8).
    if (pendingSeek === null && Math.abs(pos - playback().pos) > 0.5) next.pos = pos;
    if (Object.keys(next).length > 0) patch(next);
  }

  return {
    get element() {
      return media();
    },

    playQueue(items, index, options = {}) {
      const list = [...items];
      if (list.length === 0) return;

      const shuffle = options.shuffle ?? false;
      context = list;
      playContext = options.context ?? {};
      halted = false;
      consecutiveFailures = 0;
      lastFailed = null;
      // A deliberate new play supersedes whatever the last one had to say.
      bannerSticky = false;

      const order = shuffle ? shuffled(list) : list;
      const start = shuffle ? 0 : Math.max(0, Math.min(index, order.length - 1));
      const first = order[start] as PlayItem;

      patch({
        queue: order.slice(start + 1),
        past: order.slice(0, start),
        shuffle,
        banner: null,
      });
      // A USER gesture: this is the call that unlocks the element for the
      // whole session, so everything after it can start without one.
      void load(first, 'user');
    },

    toggle() {
      if (playback().current === null) return;
      if (media().paused) doPlay();
      else doPause();
    },

    // ABSOLUTE intents for the OS lock-screen controls (Media Session). Unlike
    // `toggle()` they cannot flip the wrong way when the store flag is stale
    // under a lock-throttle, because they act on `el.paused` directly.
    play() {
      doPlay();
    },

    pause() {
      doPause();
    },

    next() {
      const item = playback().current;
      // A manual skip is a real play with a real duration — report it before
      // the queue moves, or the listen is lost (and the SLI with it).
      if (item !== null) reportPlay(item, 'skipped');
      // A skip used to clear `halted` UNCONDITIONALLY, which handed the user a
      // way to walk straight through a 429 by pressing ⏭.
      if (blockedByCooldown()) return;
      // MANUAL-SKIP PRECEDENCE (AC 6.5): repeat-one is an `ended` behaviour,
      // so a deliberate skip advances regardless of it.
      halted = false;
      advance('user');
    },

    previous() {
      const el = media();
      const state = playback();
      const past = [...(state.past as PlayItem[])];

      // AC 6.4 — past the 3 s mark (or with nothing behind), restart.
      if (el.currentTime >= PREVIOUS_RESTART_THRESHOLD_S || past.length === 0) {
        el.currentTime = 0;
        pendingSeek = null;
        lastTickAt = 0;
        patch({ pos: 0 });
        return;
      }

      const previousItem = past.pop();
      if (previousItem === undefined) return;
      const item = state.current;
      if (item !== null) reportPlay(item, 'skipped');

      const queue = [...(state.queue as PlayItem[])];
      if (item !== null) queue.unshift(item);
      halted = false;
      patch({ past, queue });
      void load(previousItem, 'user');
    },

    seek(seconds) {
      const el = media();
      const state = playback();
      const target = Math.max(0, Math.min(seconds, state.dur > 0 ? state.dur : seconds));
      pendingSeek = target;
      pendingSeekAt = now();
      /*
       * COALESCE the element write. Every `currentTime` assignment aborts the
       * element's in-flight range request and opens a new one against the
       * lease-capped, rate-limit-exempt stream route — which is the exact shape
       * of Story 5's shipped Critical (three rapid seeks → a 90 s
       * STREAM_LIMIT self-DoS). Worse, that 429 lands on the ELEMENT's opaque
       * request, so `handlers.error` misreads it as a transport failure and
       * reloads, restarting the track at 0:00.
       *
       * The primary throttle is in `SeekBar` (a trailing debounce on `input`,
       * a commit on `change`); this is the belt to that pair of braces, and it
       * also covers the Media Session `seekto`/`seekbackward` paths.
       */
      if (lastSeekWrite === null || Math.abs(target - lastSeekWrite) >= 0.5) {
        lastSeekWrite = target;
        el.currentTime = target;
      }
      // AC 6.8 — show the REQUESTED position immediately and buffer towards
      // it. Without this the element's stale `currentTime` repaints 0:00 and
      // the scrubber visibly snaps back to the start.
      patch({ pos: target, buffering: true });
    },

    setShuffle(on) {
      const state = playback();
      if (state.shuffle === on) return;

      if (on) {
        // Reshuffle what REMAINS, leaving the current track alone: the queue is
        // then a permutation consumed once, which is the "random, no repeat
        // until all played" the AC asks for.
        patch({ shuffle: true, queue: [...shuffled(state.queue)] });
        announce('Shuffle on');
        return;
      }

      /*
       * SHUFFLE OFF RESTORES THE COLLECTION ORDER. It used to hand the same
       * shuffled array straight back — proved with a deterministic RNG:
       * `2,3,4` → shuffle on → `3,4,2` → shuffle off → still `3,4,2`. Turning a
       * mode off has to undo it, and `context` has held the original ordering
       * since `playQueue`, so the fix is to consume it: keep everything the
       * collection has that has not been played yet, in the collection's order.
       */
      const current = state.current;
      const played = new Set((state.past as PlayItem[]).map((track) => track.id));
      if (current !== null) played.add(current.id);
      const remaining = context.filter((track) => !played.has(track.id));
      // A queue that came from somewhere other than `context` (empty context,
      // or items appended later) is left alone rather than silently truncated.
      const restored =
        remaining.length === state.queue.length ? remaining : (state.queue as PlayItem[]);
      patch({ shuffle: false, queue: [...restored] });
      announce('Shuffle off');
    },

    cycleRepeat() {
      const order: RepeatMode[] = ['off', 'all', 'one'];
      const at = order.indexOf(playback().repeat);
      const next = order[(at + 1) % order.length] as RepeatMode;
      patch({ repeat: next });
      announce(`Repeat ${next}`);
      return next;
    },

    retry() {
      const item = lastFailed ?? (playback().current);
      if (item === null) return;
      // The whole point of parsing `Retry-After`: a Retry inside the window
      // issues NO request. The banner is refreshed with the remaining wait so
      // the press is answered rather than silently swallowed.
      if (blockedByCooldown()) return;
      halted = false;
      consecutiveFailures = 0;
      bannerSticky = false;
      patch({ banner: null });
      void load(item, 'user');
    },

    dispose() {
      disposed = true;
      inflight?.abort();
      if (cooldownTimer !== null) {
        clearTimer(cooldownTimer);
        cooldownTimer = null;
      }
      win.removeEventListener('pagehide', handleUnload);
      win.removeEventListener('visibilitychange', handleVisibility);
      // An in-flight load left `buffering: true` in the store with nothing
      // still running that could ever clear it — a spinner that outlives the
      // engine that raised it.
      patch({ buffering: false });
      if (element === null) return;
      for (const [type, handler] of Object.entries(handlers)) {
        element.removeEventListener(type, handler);
      }
      element.pause();
    },
  };
}

/**
 * The process-wide engine. `modules/app/index.js#startApp` drives this one, so
 * every path above has a production call site.
 */
export const audioEngine: AudioEngine = createAudioEngine();
